import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaLateRegistrationPenaltiesPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Late Registration Penalties</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Late Registration Penalties</h1>
        <p className="text-obsidian-300">What happens when your Nevada vehicle registration expires — penalties, fines, and how to resolve them.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <div className="bg-red-50 border border-red-300 rounded-xl p-6">
          <h2 className="font-display text-xl font-bold text-red-900 mb-3 flex items-center space-x-2"><i className="ri-error-warning-line text-red-600"></i><span>No Grace Period in Nevada</span></h2>
          <p className="text-red-800 text-sm">Nevada does not offer any grace period for expired vehicle registrations. Late fees begin accruing immediately after the expiration date. Driving with an expired registration can result in a traffic citation with fines up to <strong>$1,000</strong>.</p>
        </div>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Late Fee Schedule</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Penalty Type</th><th className="text-left py-2 font-semibold text-obsidian-900">How It&apos;s Calculated</th><th className="text-right py-2 font-semibold text-obsidian-900">Amount</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[
                  ['Registration Late Penalty', 'Per month past expiration', '$6.00/month'],
                  ['Tax Penalty', 'Every 15 days past expiration', '10% of past-due GST (min $6)'],
                  ['Pro-rated Taxes', 'For unregistered period', 'Varies by vehicle value'],
                  ['Traffic Citation', 'If stopped by law enforcement', 'Up to $1,000'],
                ].map(([l, h, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-obsidian-500 text-xs">{h}</td><td className="py-2.5 text-right font-medium text-red-700">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">How to Resolve Late Registration</h2>
          <ol className="space-y-4">
            {[
              { step: '1', title: 'Get Current on Insurance', desc: 'Ensure you have active Nevada vehicle insurance before attempting to renew.' },
              { step: '2', title: 'Pass Emissions (if required)', desc: 'If your vehicle requires a smog check, get it done at an authorized station.' },
              { step: '3', title: 'Pay All Outstanding Fees', desc: 'You must pay all back registration fees, late penalties, and tax penalties.' },
              { step: '4', title: 'Submit Renewal', desc: 'Renew in person at a DMV office, through an authorized provider, or online if eligible.' },
            ].map(item => (
              <li key={item.step} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-obsidian-900 text-gold-400 rounded-full font-bold text-sm flex-shrink-0">{item.step}</div>
                <div><p className="font-semibold text-obsidian-900 text-sm">{item.title}</p><p className="text-obsidian-500 text-xs mt-0.5">{item.desc}</p></div>
              </li>
            ))}
          </ol>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Non-Operation Option</h2>
          <p className="text-sm">If your vehicle is not being operated on public roads, you may file an <strong>Affidavit of Non-Operation (VP 018)</strong> before the registration expires. This can help you avoid late fees while the vehicle is not in use. The affidavit must be filed <em>before</em> the registration expires.</p>
          <Link to="/nevada-non-operation-affidavit" className="inline-flex items-center space-x-2 mt-3 text-gold-600 hover:text-gold-700 text-sm font-semibold">
            <span>Learn about Non-Operation Affidavit</span><i className="ri-arrow-right-line"></i>
          </Link>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Expired Registration? We Can Help</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can help you get back into compliance quickly. We handle all the paperwork and fees.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaLateRegistrationPenaltiesPage;
